import { Link, useNavigate } from "react-router-dom";
import logo from "../../Assets/images/logo.png";
import Dropdown from "react-bootstrap/Dropdown";
import { useCallback, useEffect, useState } from "react";
import { authService } from "../../services/authServices";
import { unitService } from "../../services/unitServices";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { MapSection } from "./MapSection";
import { CreateUnitModal } from "./CreateUnitModal";
import moment from "moment";
import { toast } from "react-toastify";
import userProfileAvtar from "../../Assets/images/avtar.png";
import { DeleteUnitModal } from "./DeleteUnitModal";
import { DeleteIcon } from "../../Assets/icons/DeleteIcon";
import { ViewIcon } from "../../Assets/icons/ViewIcon";

export const Dashboard = () => {
  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const [userProfile, setUserProfile] = useState(null);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [newUnitLocation, setNewUnitLocation] = useState(null);
  const [deleteUnit, setDeleteUnit] = useState(null);
  const [timezone, setTimezone] = useState(null);
  const [dateTime, setDateTime] = useState(new Date());

  const newDate = new Date();
  const pad = (n) => n.toString().padStart(2, "0");

  const timestamp =
    newDate.getFullYear().toString() +
    pad(newDate.getMonth() + 1) +
    pad(newDate.getDate()) +
    pad(newDate.getHours()) +
    pad(newDate.getMinutes()) +
    pad(newDate.getSeconds());

  const getFormattedTimezone = () => {
    try {
      const offsetInMinutes = new Date().getTimezoneOffset();

      const hours = Math.abs(offsetInMinutes / 60);
      const wholeHours = Math.floor(hours);
      const minutes = Math.abs(offsetInMinutes % 60);

      const sign = offsetInMinutes > 0 ? "-" : "+";

      return `GMT${sign}${String(wholeHours).padStart(2, "0")}${
        minutes ? ":" + String(minutes).padStart(2, "0") : ""
      }`;
    } catch (error) {
      console.error("Error getting timezone:", error);
      return "GMT+00:00";
    }
  };

  useEffect(() => {
    const formattedTz = getFormattedTimezone();
    setTimezone(formattedTz);
  }, []);

  const handleMapClick = useCallback((location) => {
    setNewUnitLocation(location);
  }, []);

  const handleUnitDelete = useCallback((unitId) => {
    setDeleteUnit(unitId);
  }, []);

  const handleCreateUnit = useCallback(
    async (location) => {
      setLoading(true);
      try {
        await unitService.createUnit(
          {
            lat: location.lat.toFixed(4),
            lon: location.lng.toFixed(4),
            field_location: location.address,
          },
          timestamp
        );

        const profileData = await authService.getUserProfile();
        const unitsData = await unitService.getUnitDetails(timestamp);
        if (profileData.code === 200) {
          setUserProfile(profileData.data);
        }
        if (unitsData.code === 200) {
          setUnits(unitsData.data);
        }

        setNewUnitLocation(null);
      } catch (error) {
        const errorMessage = error.response?.data?.message?.[0]?.detail;
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [timestamp]
  );

  const handleDeleteUnit = useCallback(
    async (unitId) => {
      setLoading(true);
      try {
        await unitService.deleteUnit(unitId);
        const profileData = await authService.getUserProfile();
        const unitsData = await unitService.getUnitDetails(timestamp);
        if (profileData.code === 200) {
          setUserProfile(profileData.data);
        }
        if (unitsData.code === 200) {
          setUnits(unitsData.data);
        }

        setDeleteUnit(null);
      } catch (error) {
        const errorMessage = error.response?.data?.message?.[0]?.detail;
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [timestamp]
  );

  const handleMarkerSelect = useCallback((unit) => {
    setSelectedMarker(unit);
  }, []);

  const handleLogout = async () => {
    try {
      const res = await authService.logout(token);
      if (res.code === 200) {
        localStorage.removeItem("token");
        localStorage.removeItem("selectedMonth");
        localStorage.removeItem("baseStyle");
        localStorage.removeItem("map_projection");
        navigate("/login");
      }
    } catch (error) {
      const code = error?.response?.data?.code || error?.response?.status;
      const message = error?.response?.data?.message?.[0]?.detail;
      if (code === 400 || code === 401) {
        localStorage.removeItem("token");
        navigate("/login");
        if (code !== 401) {
          toast.error(message);
        }
      }
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [profileData, unitsData] = await Promise.all([
          authService.getUserProfile(),
          unitService.getUnitDetails(timestamp),
        ]);

        if (profileData.code === 200) {
          setUserProfile(profileData.data);
        }
        if (unitsData.code === 200) {
          setUnits(unitsData.data);
        }
      } catch (error) {
        const errorMessage = error.response?.data?.message?.[0]?.detail;
        const code = error.response?.data?.code || error.response?.data?.status;
        if (code !== 401) {
          toast.error(errorMessage);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ProfileSkeleton = () => (
    <div className="d-flex gap-2 align-items-center">
      <Skeleton circle width={50} height={50} />
      <div className="d-md-block d-none">
        <Skeleton width={150} />
        <Skeleton width={120} />
      </div>
    </div>
  );

  const TableSkeleton = () => (
    <tbody>
      {[1, 2, 3, 4, 5].map((index) => (
        <tr key={index}>
          <td>
            <Skeleton width={60} />
          </td>
          <td>
            <Skeleton width={120} />
          </td>
          <td>
            <Skeleton width={80} />
          </td>
          <td>
            <Skeleton width={100} />
          </td>
          <td>
            <Skeleton width={100} />
          </td>
          <td>
            <Skeleton width={90} />
          </td>
          <td>
            <Skeleton width={90} />
          </td>
          <td>
            <Skeleton width={90} />
          </td>
          <td>
            <Skeleton width={90} />
          </td>
          <td>
            <Skeleton width={90} />
          </td>
        </tr>
      ))}
    </tbody>
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setDateTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const date = moment(dateTime).format("DD/MM/YYYY");
  const time = moment(dateTime).format("HH:mm:ss");

  const handleRowClick = useCallback((unit) => {
    setSelectedMarker(unit);
  }, []);

  return (
    <div className="main min-vh-100 d-flex justify-content-center flex-row w-100">
      <div className="page-wrapper primary-bg px-sm-4 px-3 pt-sm-4 pt-3 pb-4 w-100">
        <header className=" top-0 primary-bg">
          <div className="header-wrapper position-relative d-flex justify-content-between align-items-center">
            <div className="logo-wrapper">
              <Link to="" className="position-relative z-index-99">
                <img
                  className="logo"
                  src={logo}
                  width={239}
                  height={72}
                  alt="logo"
                />
              </Link>
            </div>
            <div className="time-wrapper d-flex align-items-center gap-lg-5 gap-3">
              <div className="d-lg-flex d-none align-items-center gap-sm-3 gap-2 pe-3">
                <div className="d-flex align-items-center time-div text-center">
                  <h4 className=" white-font d-inline-block fw-normal">
                    Current Time: 
                  </h4>
                  <h4 className="white-font font-we-sb ms-2">{date}</h4>
                </div>
                <div className="d-flex align-items-center date-div text-center">
                  {/* <h4 className=" white-font d-inline-block fw-normal">
                      Time:
                    </h4> */}
                  <h4 className="white-font font-we-sb ms-2">{time}</h4>
                </div>
                <div className="d-flex align-items-center date-div text-center">
                  {/* <h4 className=" white-font d-inline-block fw-normal">
                      Time:
                    </h4> */}
                  <h4 className="white-font font-we-sb ms-2">{timezone}</h4>
                </div>
              </div>
              <Dropdown className="custom-dropdown">
                <Dropdown.Toggle>
                  {loading ? (
                    <ProfileSkeleton />
                  ) : (
                    <div className="d-flex gap-2 align-items-center">
                      {userProfile?.profile_photo ? (
                        <img
                          src={userProfile?.profile_photo}
                          width={50}
                          height={50}
                          alt=""
                          className="profile-image"
                        />
                      ) : (
                        <img
                          src={userProfileAvtar}
                          width={50}
                          height={50}
                          alt=""
                          className="profile-image"
                        />
                      )}

                      <div className="text-start d-md-block d-none">
                        <span className="white-font font-14 w-100 d-block">
                          {`${userProfile?.first_name || ""} ${
                            userProfile?.last_name || ""
                          }`}
                        </span>
                        <span className="white-font font-14">
                          {userProfile?.email}
                        </span>
                      </div>
                    </div>
                  )}
                </Dropdown.Toggle>

                <Dropdown.Menu align="end" className="">
                  <Dropdown.Item onClick={handleLogout}>Logout</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
          <div className="d-lg-none d-flex align-items-center justify-content-center pt-3 gap-sm-3 gap-2 pe-3">
            <div className="d-flex align-items-center time-div text-center">
              <h4 className=" white-font d-inline-block fw-normal">
                Current Time: 
              </h4>
              <h4 className="white-font font-we-sb ms-2">{date}</h4>
            </div>
            <div className="d-flex align-items-center date-div text-center">
              {/* <h4 className=" white-font d-inline-block fw-normal">Time:</h4> */}
              <h4 className="white-font font-we-sb ms-2">{time}</h4>
            </div>
            <div className="d-flex align-items-center date-div text-center">
              {/* <h4 className=" white-font d-inline-block fw-normal">Time:</h4> */}
              <h4 className="white-font font-we-sb ms-2">{timezone}</h4>
            </div>
          </div>
        </header>
        <div className="content">
          <div className="d-flex flex-column gap-xl-4 gap-3 ">
            <div className="row gap-lg-0 gap-3">
              <div className="col-12 h-100">
                <div className="secondary-bg custom-rounded card d-flex flex-column h-100 gap-3">
                  <div className="row">
                    <div className="col-xl-9 col-lg-8 col-md-7 col-12">
                      <div className="d-flex justify-content-between flex-column h-100 gap-3">
                        <div>
                          <h2 className="white-font font-we-nr pb-lg-2">
                            Solar Units - Location
                          </h2>
                        </div>
                        <div className="dashboard-map-wrapper">
                          {loading ? (
                            <Skeleton height={500} />
                          ) : (
                            <MapSection
                              units={units}
                              selectedMarker={selectedMarker}
                              onMarkerSelect={handleMarkerSelect}
                              onMapClick={handleMapClick} // Add this prop
                              onDeleteUnit={handleUnitDelete}
                            />
                          )}
                          <CreateUnitModal
                            show={!!newUnitLocation}
                            location={newUnitLocation}
                            onClose={() => setNewUnitLocation(null)}
                            onConfirm={handleCreateUnit}
                            disabled={loading}
                          />
                          <DeleteUnitModal
                            show={!!deleteUnit}
                            unit={deleteUnit}
                            onClose={() => setDeleteUnit(null)}
                            onConfirm={handleDeleteUnit}
                            disabled={loading}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-xl-3 col-lg-4 col-md-5 col-12">
                      <div className="d-flex justify-content-between flex-column h-100 gap-3 pt-lg-0 pt-4">
                        <div>
                          <h2 className="white-font font-we-nr pb-lg-2">
                            Summary
                          </h2>
                        </div>
                        <div className="dashboard-map-wrapper common-table-wrapper">
                          {loading ? (
                            <Skeleton height={500} />
                          ) : (
                            <div className="text-white px-lg-3 px-2 py-4">
                              <div className="row mb-3 pb-2">
                                <div className="col-6">
                                  <span className="h_4 line-height-normal w-100 d-inline-block">
                                    User Name:
                                  </span>
                                </div>
                                <div className="col-6">
                                  <span className="h_4 line-height-normal w-100 d-inline-block">
                                    {`${userProfile?.first_name} ${userProfile?.last_name}`}
                                  </span>
                                </div>
                              </div>
                              <div className="row mb-3 pb-2">
                                <div className="col-6">
                                  <span className="h_4 line-height-normal w-100 d-inline-block">
                                    Organization Name:
                                  </span>
                                </div>
                                <div className="col-6">
                                  <span className="h_4 line-height-normal w-100 d-inline-block">
                                    {userProfile?.org_name}
                                  </span>
                                </div>
                              </div>
                              <div className="row mb-3 pb-2">
                                <div className="col-6">
                                  <span className="h_4 line-height-normal w-100 d-inline-block">
                                    Total Units:
                                  </span>
                                </div>
                                <div className="col-6">
                                  <span className="h_4 line-height-normal w-100 d-inline-block">
                                    {userProfile?.user_unit}
                                  </span>
                                </div>
                              </div>
                              <div className="row mb-3 pb-2">
                                <div className="col-6">
                                  <span className="h_4 line-height-normal w-100 d-inline-block">
                                    Active Units:
                                  </span>
                                </div>
                                <div className="col-6">
                                  <span className="h_4 line-height-normal w-100 d-inline-block">
                                    {userProfile?.user_active_unit}
                                  </span>
                                </div>
                              </div>
                              <div className="row">
                                <div className="col-6">
                                  <span className="h_4 line-height-normal w-100 d-inline-block">
                                    Inactive Units:
                                  </span>
                                </div>
                                <div className="col-6">
                                  <span className="h_4 line-height-normal w-100 d-inline-block">
                                    {userProfile?.user_inactive_unit}
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="row inner-content2 gap-lg-0 gap-3">
              <div className="col-12 h-100">
                <div className="secondary-bg custom-rounded card d-flex flex-column h-100 gap-3">
                  <div className="d-flex justify-content-between flex-column h-100 gap-3">
                    <div>
                      <h2 className="white-font font-we-nr pb-lg-2">
                        Solar Unit Details
                      </h2>
                    </div>
                    <div className="common-table-wrapper">
                      <div className="table-responsive h-100">
                        <table className="table common-table">
                          <thead>
                            <tr>
                              <th className="text-nowrap">ID</th>
                              <th className="text-nowrap">Field Location</th>
                              <th className="text-nowrap">Type</th>
                              <th className="text-nowrap">Co-ordinates</th>
                              <th className="text-nowrap">
                                Last Message Received
                              </th>
                              <th className="text-nowrap">Status</th>
                              <th className="text-nowrap">Power</th>
                              <th className="text-nowrap">Battery Voltage</th>
                              <th className="text-nowrap">Battery (%)</th>
                              <th className="text-nowrap">Action</th>
                            </tr>
                          </thead>
                          {loading ? (
                            <TableSkeleton />
                          ) : (
                            <tbody>
                              {units?.results?.map((unit, index) => (
                                <tr
                                  key={index}
                                  onClick={() => handleRowClick(unit)}
                                  className={`cursor-pointer unit-table-row ${
                                    selectedMarker?.unit_name === unit.unit_name
                                      ? "selected-row"
                                      : ""
                                  }`}
                                >
                                  <td>{unit.unit_name}</td>
                                  <td>{unit.field_location || "Germany"}</td>
                                  <td>{unit.unit_type}</td>
                                  <td>
                                    {parseFloat(unit.lat).toFixed(1)},{" "}
                                    {parseFloat(unit.lon).toFixed(1)}
                                  </td>
                                  <td>{unit.last_message}</td>
                                  <td
                                    className={
                                      unit.status === "ACTIVE"
                                        ? "status-active"
                                        : "status-inactive"
                                    }
                                  >
                                    {unit.status}
                                  </td>
                                  <td>{unit.output} Watt</td>
                                  <td>{unit.battery_voltage} Volt</td>
                                  <td>{unit.btry_volt_per}%</td>

                                  <td className="d-flex gap-2">
                                    <Link
                                      to={`/unit-details/${unit.id}`}
                                      className="text-underline"
                                    >
                                      <ViewIcon />
                                    </Link>
                                    {!unit.is_default && (
                                      <span
                                        className="text-underline cursor-pointer"
                                        onClick={() =>
                                          handleUnitDelete(unit.id)
                                        }
                                      >
                                        <DeleteIcon />
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          )}
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
